import { computed, type Ref, type WritableComputedRef } from "vue";
import cloneDeep from "lodash/cloneDeep";

/**
 * Creates two-way binding model with the same structure as BE response. Uses callback to update value (HTTP request in store).
 * @param initialValue - An initial value of the model. Should be a Ref.
 * @param updateCallback - Callback which will be called when a resulting variable will be updated.
 * @param restCallbackParameters - Rest of the parameters that are needed for the callback.
 */
export function useTwoWayBinding<T extends object, K extends unknown[], V>(
  initialValue: Ref<T>,
  updateCallback: (value: T, ...args: K) => V,
  ...restCallbackParameters: K
): WritableComputedRef<T> {
  function createDeepProxy(obj: T): T {
    return new Proxy(obj, {
      get(target, prop, receiver) {
        const result = Reflect.get(target, prop, receiver);
        if (typeof result === "object" && result !== null) {
          return createDeepProxy(result as T);
        }
        return result;
      },
      set(target, prop, value, receiver) {
        const result = Reflect.set(target, prop, value, receiver);
        // Trigger the update callback with a deep-cloned updated value
        updateCallback(cloneDeep(initialValue.value), ...restCallbackParameters);
        return result;
      },
    });
  }

  return computed<T>({
    get() {
      return createDeepProxy(initialValue.value);
    },
    set(newValue: T) {
      updateCallback(newValue, ...restCallbackParameters);
    },
  });
}
